import React, { useRef, useState } from "react";
import { usePagination, useTable } from "react-table";
import double_before from "../../../../assets/vw/svg/double-before.svg";
import before from "../../../../assets/vw/svg/before.svg";
import after from "../../../../assets/vw/svg/after.svg";
import double_after from "../../../../assets/vw/svg/double-after.svg";
import SEARCH from "../../../../assets/vw/svg/search.svg";
import blue_file_icon from "../../../../assets/vw/svg/blue-file-icon.svg";
import upload_icon from "../../../../assets/vw/svg/upload-icon.svg";
import copy_icon from "../../../../assets/vw/svg/copy-icon.svg";
import paste_icon from "../../../../assets/vw/svg/paste-icon.svg";
import DropDown from "../../../../components/Dropdown";
import DropdownSearch from "../../../../components/DropdownSearch";
import cerrar from "../../../../assets/vw/svg/cerrar.svg";
import InputText from "../../../../components/InputText";
import ModalWarning from "../../../../components/ModalWarning";
import download_icon from "../../../../assets/vw/svg/download-white.svg";
import eliminar_icon from "../../../../assets/vw/svg/eliminar-white.svg";
import preview_icon from "../../../../assets/vw/svg/eye.svg";
import editar_icon from "../../../../assets/vw/svg/editar_blanco.svg";
import mover_icon from "../../../../assets/vw/svg/mover-blanco.svg";
import menu_opc from "../../../../assets/vw/svg/menu-opciones.svg";
import Cards from "../Cards";
import { FileTypeIcon, getLibraryItemName, isImageFile } from "../fileIcons";

const mockArchivos = [
  {
    ETag: '"70ee1738b6b21e2c8a43f3a5ab0eee71"',
    Key: "example1.jpg",
    LastModified: "2014-11-21T19:40:05.000Z",
    Owner: {
      DisplayName: "myname",
      ID: "12345example25102679df27bb0ae12b3f85be6f290b936c4393484be31bebcc",
    },
    Size: 11,
    StorageClass: "STANDARD",
  },
  {
    ETag: '"9c8af9a76df052144598c115ef33e511"',
    Key: "example2.jpg",
    LastModified: "2013-11-15T01:10:49.000Z",
    Owner: {
      DisplayName: "myname",
      ID: "12345example25102679df27bb0ae12b3f85be6f290b936c4393484be31bebcc",
    },
    Size: 713193,
    StorageClass: "STANDARD",
  },
];

const getProjectName = (key = "") => key.split("/").filter(Boolean)[0] || key;

const getItemName = (archivo) => archivo?.__prefix || archivo?.Key || "este elemento";

const getActionTarget = (archivo = {}) => archivo.__isPrefix ? archivo : archivo.__source || archivo;

const isFolderItem = (archivo) => archivo?.__isPrefix || archivo?.Key?.endsWith("/");

const splitFileName = (name = "") => {
  const dotIndex = name.lastIndexOf(".");

  if (dotIndex <= 0) {
    return { baseName: name, extension: "" };
  }

  return {
    baseName: name.slice(0, dotIndex),
    extension: name.slice(dotIndex),
  };
};

const getDeleteMessage = (archivo) => {
  if (isFolderItem(archivo)) {
    return `El elemento ${getItemName(archivo)} y todo su contenido se eliminarán permanentemente.\nEsta acción no se puede deshacer.`;
  }

  return `El elemento ${getItemName(archivo)} se eliminará permanentemente.\nEsta acción no se puede deshacer.`;
};

const getMoveItemType = (archivo) => isFolderItem(archivo) ? "carpeta" : "archivo";

const getRelativeKey = (key = "", prefix = "") => {
  if (!prefix || !key.startsWith(prefix)) return key;
  return key.slice(prefix.length);
};

const normalizeFolderKey = (path = "") => {
  const normalizedPath = path.replace(/^\/+/, "").replace(/\/+/g, "/").replace(/\/$/, "");
  return normalizedPath ? `${normalizedPath}/` : "";
};

const hasInvalidName = (name = "") => !name.trim() || name.includes("/");

const getLatestDate = (currentDate, nextDate) => {
  if (!currentDate) return nextDate;
  if (!nextDate) return currentDate;
  return new Date(nextDate) > new Date(currentDate) ? nextDate : currentDate;
};

const buildTableRows = (archivos, currentPrefix) => {
  const rowsByKey = new Map();

  if (currentPrefix) {
    archivos.forEach((archivo) => {
      if (!archivo.Key?.startsWith(currentPrefix)) return;

      const relativeKey = getRelativeKey(archivo.Key, currentPrefix);
      if (!relativeKey) return;

      const [childName] = relativeKey.split("/");
      const isPrefix = relativeKey.includes("/");
      const rowKey = isPrefix ? childName : relativeKey;

      if (!rowKey) return;

      if (rowsByKey.has(rowKey)) {
        const currentRow = rowsByKey.get(rowKey);
        rowsByKey.set(rowKey, {
          ...currentRow,
          Size: isPrefix ? (currentRow.Size || 0) + (archivo.Size || 0) : currentRow.Size,
          LastModified: getLatestDate(currentRow.LastModified, archivo.LastModified),
        });
        return;
      }

      rowsByKey.set(rowKey, {
        ...archivo,
        Key: rowKey,
        Size: archivo.Size,
        __isPrefix: isPrefix,
        __prefix: isPrefix ? `${currentPrefix}${childName}/` : "",
        __source: archivo,
      });
    });

    return Array.from(rowsByKey.values());
  }

  archivos.forEach((archivo) => {
    const [prefix] = (archivo.Key || "").split("/");
    const isPrefix = Boolean(prefix && archivo.Key?.includes("/"));
    const rowKey = isPrefix ? prefix : archivo.Key;

    if (!rowKey) return;

    if (rowsByKey.has(rowKey)) {
      const currentRow = rowsByKey.get(rowKey);
      rowsByKey.set(rowKey, {
        ...currentRow,
        Size: isPrefix ? (currentRow.Size || 0) + (archivo.Size || 0) : currentRow.Size,
        LastModified: getLatestDate(currentRow.LastModified, archivo.LastModified),
      });
      return;
    }

    rowsByKey.set(rowKey, {
      ...archivo,
      Key: rowKey,
      Size: archivo.Size,
      __isPrefix: isPrefix,
      __prefix: isPrefix ? `${prefix}/` : "",
      __source: archivo,
    });
  });

  return Array.from(rowsByKey.values());
};

const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("es-MX");
};

const formatBytes = (bytes) => {
  if (bytes === undefined || bytes === null) return "";
  if (bytes === 0) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const sizeIndex = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, sizeIndex);

  return `${size.toFixed(sizeIndex === 0 ? 0 : 1)} ${units[sizeIndex]}`;
};

function Table({
  columns,
  data,
  handleChange = () => {},
  onNuevo = () => {},
  onSubir = () => {},
  onNuevaCarpeta = () => {},
  onDescargar = () => {},
  onEliminar = () => {},
  onMover = () => {},
  onCopiar = () => {},
  onPegar = () => {},
  onRenombrar = () => {},
  onPreview = () => {},
  onRowClick = () => {},
  currentPrefix = "",
  canPaste = false,
}) {
  const [limit, setLimit] = useState(15);
  const [searchInput, setSearchInput] = useState("");
  const [showGrid, setShowGrid] = useState(false);
  const [modal, setModal] = useState(false);
  const [modalTitle, setModalTitle] = useState(currentPrefix ? "Nueva carpeta" : "Nuevo proyecto");
  const [nombre, setNombre] = useState("");
  const fileInputRef = useRef(null);
  const limitMostrar = ["Mostrar 15", "Mostrar 30", "Mostrar 50", "Todo"];
  const filteredData = React.useMemo(() => {
    const search = searchInput.trim().toLowerCase();
    if (!search) return data;

    return data.filter((item) => [
      getLibraryItemName(item),
      item.Key,
      item.LastModified,
      item.Size,
    ].join(" ").toLowerCase().includes(search));
  }, [data, searchInput]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageSize: 15 },
    },
    usePagination
  );

  function searchText(e) {
    setSearchInput(e.target.value);
    handleChange(e);
  }

  const optionLimit = (textoLimit) => {
    switch (textoLimit) {
      case "Mostrar 15":
        return 15;
      case "Mostrar 30":
        return 30;
      case "Mostrar 50":
        return 50;
      case "Todo":
        return filteredData.length || 15;
      default:
        return filteredData.length || 15;
    }
  };

  function newProject() {
    setModalTitle(currentPrefix ? "Nueva carpeta" : "Nuevo proyecto");
    setModal(true);
    setNombre("");
  }

  function newFolder() {
    setModalTitle("Nueva carpeta");
    setModal(true);
    setNombre("");
  }

  function cancelar() {
    setModal(false);
    setNombre("");
  }

  async function saveNewProject() {
    const nombreLimpio = nombre.trim();
    if (hasInvalidName(nombreLimpio)) return;

    if (modalTitle === "Nueva carpeta") {
      onNuevaCarpeta(nombreLimpio);
    } else {
      onNuevo(nombreLimpio);
    }
    cancelar();
  }

  function openFileExplorer() {
    fileInputRef.current?.click();
  }

  function handleFileSelected(event) {
    const [file] = event.target.files || [];
    if (file) onSubir(file);
    event.target.value = "";
  }

  const buildPopUpNewProject = () => {
    return (
      <div
        className={`modal fade modal_vw  ${modal ? "show" : ""}`}
        id="modal"
        data-keyboard="false"
        data-backdrop="static"
        tabIndex="-1"
        aria-labelledby="desarchivarLabel"
        style={modal ? { display: "block" } : {}}
        aria-modal="true"
        role="dialog"
    >
        <div className="modal-dialog modal-dialog-centered modal_dialog_fixed--mobile modal_lg_vw" role="document">
          <div className="modal-content modal_content-vw modal_content_fixed--mobile p-0 p-lg-2">
            <div className="modal-header modal_header-vw ">
              <div className="d-flex justify-content-end">
                <button
                  className="button_icon-vw"
                  type="button"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={() => {
                    cancelar();
                  }}
                >
                  <img src={cerrar} alt="cerrar"></img>
                </button>
              </div>
            </div>
            <div className="modal-body mx-3 mx-sm-4 mx-lg-5 p-0 mb-2 mb-sm-4 mb-lg-0">
              <div className="modal-title-container d-flex justify-content-start titulo pb-2">
                <span className={"modal-title-principal-text"}>{modalTitle}</span>
              </div>
              <div className=" border_vw"></div>

              <div className="row mt-4 p-0 mx-0">
                <div className="col-12 p-0">
                <InputText
                 inputLabel={modalTitle === "Nueva carpeta" ? "Nombre de la carpeta" : "Nombre del proyecto"}
                 inputField={nombre}
                 id={"nombre"}
                 changingInput ={(event)=>{
                     setNombre(event.target.value);
                 }}
                   />

                </div>
              </div>
            </div>
            <div className="modal-footer footer_modal_vw d-flex justify-content-center justify-content-lg-end gap-2 gap-sm-3 gap-lg-2 px-0 mx-3 mx-lg-5 mb-2 mb-lg-0">
              <button
                type="button"
                className="button_vw button_vw--secondary order-2 order-sm-1"
                data-bs-dismiss="modal"
                onClick={cancelar}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="button_vw button_vw--primary order-1 order-sm-2"
                data-bs-dismiss="modal"
                onClick={() => saveNewProject()}
                disabled={hasInvalidName(nombre)}
                data-track-click="btn-guardar-rol"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const NavigationControl = () => {
    return (
      <div className="btn-group btn-group-controls" role="group" aria-label="First group">
        <button
          onClick={() => gotoPage(0)}
          disabled={!canPreviousPage}
          className="btn btn-secondary btn__secondary-- btn_pagination"
        >
          <img src={double_before} alt="first" />
        </button>{" "}
        <button
          onClick={() => previousPage()}
          disabled={!canPreviousPage}
          className="btn btn-secondary btn__secondary-- btn_pagination"
        >
          <img src={before} alt="before" />
        </button>{" "}
        <div className="num_pagination py-3">
          {pageOptions.length === 0 ? 0 : pageIndex + 1} de {pageOptions.length}
        </div>
        <button
          onClick={() => nextPage()}
          disabled={!canNextPage}
          className="btn btn-secondary btn__secondary-- btn_pagination"
        >
          <img src={after} alt="after" />
        </button>{" "}
        <button
          onClick={() => gotoPage(pageCount - 1)}
          disabled={!canNextPage}
          className="btn btn-secondary btn__secondary-- btn_pagination"
        >
          <img src={double_after} alt="last" />
        </button>{" "}
      </div>
    );
  };

  return (
    <>
      <div className="content main-content-container">
        <input
          ref={fileInputRef}
          type="file"
          className="d-none"
          onChange={handleFileSelected}
        />
        <div className="main-content-actions row m-0 py-2">
          <div className="col-7 col-sm-6 col-lg-3 wrapper m-0 p-0">
            <input
              className="input_vw"
              name="searchInput"
              value={searchInput || ""}
              onChange={searchText}
              placeholder="Busca por nombre, fecha..."
            />
            <span className="pt-2 ps-1">
              <img src={SEARCH} alt="buscar" className="icono-menu me-3" />
            </span>
          </div>

          <div className={`d-flex col-1 col-sm-1 ${canPaste ? "col-lg-5" : "col-lg-3"} gap-2`}>
            {currentPrefix ? (
              <>
                <button
                  type="button"
                  onClick={openFileExplorer}
                  className="m-0 button_vw button_vw--primary button_new_item enabled"
                  data-track-click="btn-subir-archivo"
                >
                  <img src={upload_icon} alt="subir" />
                  <span className="ml-2">Subir</span>
                </button>
                <button
                  type="button"
                  onClick={newFolder}
                  className="m-0 button_vw_proyecto button_vw_proyecto--archivo-azul button_unarchive_item"
                  data-track-click="btn-nueva-carpeta"
                >
                  <img src={blue_file_icon} alt="carpeta" />
                  <span className="ml-2">Nueva carpeta</span>
                </button>
                {canPaste && (
                  <button
                    type="button"
                    onClick={onPegar}
                    className="m-0 button_vw_proyecto button_vw_proyecto--archivo-azul button_unarchive_item"
                    data-track-click="btn-pegar-archivo"
                  >
                    <img src={paste_icon} alt="pegar" />
                    <span className="ml-2">Pegar</span>
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={newProject}
                  className="button_vw_proyecto button_vw_proyecto--archivo-azul button_unarchive_item"
                  data-track-click="btn-nuevo-proyecto"
                >
                  <img src={blue_file_icon} alt="proyecto" />
                  <span className="ml-2">Nuevo proyecto</span>
                </button>
                {canPaste && (
                  <button
                    type="button"
                    onClick={onPegar}
                    className="button_vw_proyecto button_vw_proyecto--archivo-azul button_unarchive_item"
                    data-track-click="btn-pegar-archivo"
                  >
                    <img src={paste_icon} alt="pegar" />
                    <span className="ml-2">Pegar</span>
                  </button>
                )}
              </>
            )}
          </div>

          <div className={`col-3 col-sm-5 ${canPaste ? "col-lg-4" : "col-lg-6"} p-0`}>
            <div className="d-flex justify-content-end align-items-center">
              <div className="view__options">
                <button
                  className="button_icon-vw kit_icon"
                  type="button"
                  onClick={() => setShowGrid(true)}
                >
                  <span className={showGrid ? "icon-grid-active" : "icon-grid"}> </span>
                </button>
                <button
                  className="button_icon-vw kit_icon"
                  type="button"
                  onClick={() => setShowGrid(false)}
                >
                  <span className={!showGrid ? "icon-table-active" : "icon-table"}> </span>
                </button>
              </div>
              <div>
                <DropDown
                  menu={limitMostrar}
                  title={limitMostrar[0]}
                  inputField={limit}
                  handleOptionSelected={(e) => {
                    const limitInt = optionLimit(e);
                    setLimit(limitInt);
                    setPageSize(Number(limitInt));
                  }}
                />
              </div>
              <NavigationControl />
            </div>
          </div>
        </div>
      </div>

      {showGrid ? (
        <Cards
          page={page}
            onRowClick={onRowClick}
            onDescargar={onDescargar}
            onEliminar={onEliminar}
            onMover={onMover}
            onCopiar={onCopiar}
            onRenombrar={onRenombrar}
            onPreview={onPreview}
            currentPrefix={currentPrefix}
          />
      ) : (
        <div
          className="table-responsive"
          style={{
            overflowY: "hidden",
          }}
        >
          <table
            {...getTableProps()}
            className="table table-striped table-responsive tabla_contenido_vw"
            id="table-libreria"
          >
            <thead className="thead-dark">
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()} className="thead__titulo">
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps()} className={column.id === "accion" ? "text-center" : ""}>{column.render("Header")}</th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()} className="tbody-table">
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    className="table-tr-check"
                    onClick={() => onRowClick(row.original)}
                  >
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {buildPopUpNewProject()}
    </>
  );
}

function TablaArchivos({
  archivos = mockArchivos,
  handleChange,
  onNuevo,
  onSubir = () => console.log("SUBIR ARCHIVO"),
  onNuevaCarpeta = () => console.log("NUEVA CARPETA"),
  onDescargar = (archivo) => console.log("DESCARGAR ARCHIVO: ", archivo),
  onEliminar = (archivo) => console.log("ELIMINAR ARCHIVO: ", archivo),
  onMover = (archivo, destino) => console.log("MOVER ARCHIVO: ", archivo, destino),
  onCopiar = (archivo) => console.log("COPIAR ARCHIVO: ", archivo),
  onPegar = () => console.log("PEGAR ARCHIVO"),
  onRenombrar = (archivo, nombre) => console.log("RENOMBRAR ARCHIVO: ", archivo, nombre),
  onListarCarpetas = async () => [],
  onPreview = (archivo) => console.log("VISTA PREVIA ARCHIVO: ", archivo),
  currentPrefix = "",
  canPaste = false,
  setCurrentPrefix = () => {},
}) {
  const data = archivos;
  const tableRows = React.useMemo(
    () => buildTableRows(data, currentPrefix),
    [data, currentPrefix]
  );
  const [modalEliminar, setModalEliminar] = React.useState({
    active: false,
    item: null,
  });
  const [moveModal, setMoveModal] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [moveProjects, setMoveProjects] = React.useState([]);
  const [moveFolders, setMoveFolders] = React.useState([]);
  const [moveSubfolders, setMoveSubfolders] = React.useState([]);
  const [selectedMoveProject, setSelectedMoveProject] = React.useState(null);
  const [selectedMoveFolder, setSelectedMoveFolder] = React.useState(null);
  const [selectedMoveSubfolder, setSelectedMoveSubfolder] = React.useState(null);
  const [renameModal, setRenameModal] = React.useState(false);
  const [renameName, setRenameName] = React.useState("");
  const [selectedRenameItem, setSelectedRenameItem] = React.useState(null);
  const [renameExtension, setRenameExtension] = React.useState("");
  const [moveFolderModal, setMoveFolderModal] = React.useState(false);
  const [moveFolderName, setMoveFolderName] = React.useState("");

  const handleEliminar = React.useCallback((archivo) => {
    setModalEliminar({
      active: true,
      item: archivo,
    });
  }, []);

  const closeDeleteModal = () => {
    setModalEliminar({
      active: false,
      item: null,
    });
  };

  const confirmDelete = () => {
    if (modalEliminar.item) return onEliminar(modalEliminar.item);
  };

  const openMoveModal = React.useCallback(async (archivo) => {
    setSelectedItem(archivo);
    setSelectedMoveProject(null);
    setSelectedMoveFolder(null);
    setSelectedMoveSubfolder(null);
    setMoveFolders([]);
    setMoveSubfolders([]);
    setMoveModal(true);
    try {
      const proyectos = await onListarCarpetas("");
      setMoveProjects(proyectos);
    } catch (error) {
      console.error(error.stack || error);
      setMoveProjects([]);
    }
  }, [onListarCarpetas]);

  const closeMoveModal = () => {
    setSelectedItem(null);
    setSelectedMoveProject(null);
    setSelectedMoveFolder(null);
    setSelectedMoveSubfolder(null);
    setMoveProjects([]);
    setMoveFolders([]);
    setMoveSubfolders([]);
    setMoveModal(false);
  };

  const getSelectedMoveDestination = () => (
    selectedMoveSubfolder?.value || selectedMoveFolder?.value || selectedMoveProject?.value || ""
  );

  const getMoveFolderOption = (name, parentPath) => ({
    label: name,
    value: normalizeFolderKey(`${normalizeFolderKey(parentPath)}${name}`),
  });

  const getDropdownSearchValue = (event) => event?.props?.value || "";

  const selectMoveProject = async (event) => {
    const selectedValue = getDropdownSearchValue(event);
    const selectedProject = moveProjects.find((project) => project.value === selectedValue);
    setSelectedMoveProject(selectedProject || null);
    setSelectedMoveFolder(null);
    setSelectedMoveSubfolder(null);
    setMoveFolders([]);
    setMoveSubfolders([]);

    if (!selectedProject) return;

    try {
      const carpetas = await onListarCarpetas(selectedProject.value);
      setMoveFolders(carpetas);
    } catch (error) {
      console.error(error.stack || error);
      setMoveFolders([]);
    }
  };

  const selectMoveFolder = async (event) => {
    const selectedValue = getDropdownSearchValue(event);
    const selectedFolder = moveFolders.find((folder) => folder.value === selectedValue);
    setSelectedMoveFolder(selectedFolder || null);
    setSelectedMoveSubfolder(null);
    setMoveSubfolders([]);

    if (!selectedFolder) return;

    try {
      const subcarpetas = await onListarCarpetas(selectedFolder.value);
      setMoveSubfolders(subcarpetas);
    } catch (error) {
      console.error(error.stack || error);
      setMoveSubfolders([]);
    }
  };

  const selectMoveSubfolder = (event) => {
    const selectedValue = getDropdownSearchValue(event);
    const selectedSubfolder = moveSubfolders.find((subfolder) => subfolder.value === selectedValue);
    setSelectedMoveSubfolder(selectedSubfolder || null);
  };

  const saveMove = () => {
    onMover(selectedItem, getSelectedMoveDestination());
    closeMoveModal();
  };

  const closeMoveFolderModal = () => {
    setMoveFolderModal(false);
    setMoveFolderName("");
  };

  const saveMoveFolder = async () => {
    const nombreLimpio = moveFolderName.trim();
    const parentPath = getSelectedMoveDestination();

    if (hasInvalidName(nombreLimpio) || selectedMoveSubfolder) return;

    const createdFolder = getMoveFolderOption(nombreLimpio, parentPath);
    const created = await onNuevaCarpeta(nombreLimpio, parentPath);
    if (!created) return;

    if (!parentPath) {
      const proyectos = await onListarCarpetas("");
      setMoveProjects(proyectos);
      setSelectedMoveProject(createdFolder);
      setMoveFolders([]);
      setSelectedMoveFolder(null);
      setMoveSubfolders([]);
      setSelectedMoveSubfolder(null);
    } else if (selectedMoveFolder) {
      const subcarpetas = await onListarCarpetas(selectedMoveFolder.value);
      setMoveSubfolders(subcarpetas);
      setSelectedMoveSubfolder(createdFolder);
    } else if (selectedMoveProject) {
      const carpetas = await onListarCarpetas(selectedMoveProject.value);
      setMoveFolders(carpetas);
      setSelectedMoveFolder(createdFolder);
      setMoveSubfolders([]);
      setSelectedMoveSubfolder(null);
    }

    closeMoveFolderModal();
  };

  const openRenameModal = React.useCallback((archivo) => {
    const itemName = getLibraryItemName(archivo);
    const { baseName, extension } = splitFileName(itemName);

    setSelectedRenameItem(archivo);
    setRenameName(isFolderItem(archivo) ? getProjectName(itemName) : baseName);
    setRenameExtension(isFolderItem(archivo) ? "" : extension);
    setRenameModal(true);
  }, []);

  const closeRenameModal = () => {
    setSelectedRenameItem(null);
    setRenameName("");
    setRenameExtension("");
    setRenameModal(false);
  };

  const saveRename = () => {
    const nombreLimpio = renameName.trim();
    if (hasInvalidName(nombreLimpio)) return;

    onRenombrar(selectedRenameItem, `${nombreLimpio}${renameExtension}`);
    closeRenameModal();
  };

  const buildPopUpMove = () => (
    <div
      className={`modal fade modal_vw  ${moveModal ? "show" : ""}`}
      id="modal-mover"
      data-keyboard="false"
      data-backdrop="static"
      tabIndex="-1"
      aria-labelledby="moverLabel"
      style={moveModal ? { display: "block" } : {}}
      aria-modal="true"
      role="dialog"
    >
      <div className="modal-dialog modal-dialog-centered modal_lg_vw libreria-move-modal-dialog" role="document">
        <div className="modal-content modal_content-vw libreria-move-modal p-0 p-lg-2">
          <div className="modal-header modal_header-vw ">
            <div className="d-flex justify-content-end">
              <button className="button_icon-vw" type="button" data-bs-dismiss="modal" aria-label="Close" onClick={closeMoveModal}>
                <img src={cerrar} alt="cerrar"></img>
              </button>
            </div>
          </div>
          <div className="modal-body mx-3 mx-sm-4 mx-lg-5 p-0 mb-2 mb-sm-4 mb-lg-0">
            <div className="modal-title-container d-flex justify-content-start titulo pb-2">
              <span className={"modal-title-principal-text"}>Mover a</span>
            </div>
            <div className=" border_vw"></div>
            <p className="text-general-vw mt-3 mb-0">
              Selecciona el proyecto y/o carpeta donde deseas mover la {moveModal ? getMoveItemType(selectedItem) : "archivo"} <strong>{moveModal ? getLibraryItemName(selectedItem) : ""}</strong>.
            </p>
            <div className="row mt-3 p-0 mx-0 align-items-start gx-4 gy-4">
              <div className="col-12 col-lg-6 ps-0 pe-lg-4 mb-3">
                <DropdownSearch
                  menu={moveProjects}
                  title="Selecciona un proyecto"
                  defaultValue={selectedMoveProject?.value || ""}
                  handleOptionSelected={selectMoveProject}
                  fieldID="value"
                  fieldToShow="label"
                  extraStyle="w-100"
                />
              </div>
              <div className="col-12 col-lg-6 pe-0 ps-lg-4 mb-3">
                <DropdownSearch
                  menu={moveFolders}
                  title="Selecciona una carpeta"
                  defaultValue={selectedMoveFolder?.value || ""}
                  handleOptionSelected={selectMoveFolder}
                  fieldID="value"
                  fieldToShow="label"
                  extraStyle="w-100"
                  disabled={!selectedMoveProject || moveFolders.length === 0}
                />
              </div>
              <div className="col-12 col-lg-6 ps-0 pe-lg-4">
                <DropdownSearch
                  menu={moveSubfolders}
                  title="Selecciona una subcarpeta"
                  defaultValue={selectedMoveSubfolder?.value || ""}
                  handleOptionSelected={selectMoveSubfolder}
                  fieldID="value"
                  fieldToShow="label"
                  extraStyle="w-100"
                  disabled={!selectedMoveFolder || moveSubfolders.length === 0}
                />
              </div>
            </div>
          </div>
          <div className="modal-footer footer_modal_vw d-flex justify-content-between gap-2 gap-sm-3 gap-lg-2 px-0 mx-3 mx-lg-5 mb-2 mb-lg-0 mt-lg-4">
            <button
              type="button"
              className="button_vw_proyecto button_vw_proyecto--archivo-azul button_unarchive_item"
              onClick={() => setMoveFolderModal(true)}
              disabled={selectedMoveSubfolder}
              data-track-click="btn-mover-nueva-carpeta"
            >
              <img src={blue_file_icon} alt="carpeta" />
              <span className="ml-2">Nueva carpeta</span>
            </button>
            <div className="d-flex gap-2 gap-sm-3 gap-lg-2">
              <button type="button" className="button_vw button_vw--secondary" data-bs-dismiss="modal" onClick={closeMoveModal}>Cancelar</button>
              <button type="button" className="button_vw button_vw--primary" data-bs-dismiss="modal" onClick={saveMove} disabled={!getSelectedMoveDestination()} data-track-click="btn-mover-archivo-guardar">Guardar</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const buildPopUpMoveFolder = () => (
    <div
      className={`modal fade modal_vw  ${moveFolderModal ? "show" : ""}`}
      id="modal-mover-nueva-carpeta"
      data-keyboard="false"
      data-backdrop="static"
      tabIndex="-1"
      aria-labelledby="moverNuevaCarpetaLabel"
      style={moveFolderModal ? { display: "block" } : {}}
      aria-modal="true"
      role="dialog"
    >
      <div className="modal-dialog modal-dialog-centered modal_dialog_fixed--mobile modal_lg_vw" role="document">
        <div className="modal-content modal_content-vw modal_content_fixed--mobile p-0 p-lg-2">
          <div className="modal-header modal_header-vw ">
            <div className="d-flex justify-content-end">
              <button className="button_icon-vw" type="button" data-bs-dismiss="modal" aria-label="Close" onClick={closeMoveFolderModal}>
                <img src={cerrar} alt="cerrar"></img>
              </button>
            </div>
          </div>
          <div className="modal-body mx-3 mx-sm-4 mx-lg-5 p-0 mb-2 mb-sm-4 mb-lg-0">
            <div className="modal-title-container d-flex justify-content-start titulo pb-2">
              <span className={"modal-title-principal-text"}>Nueva carpeta</span>
            </div>
            <div className=" border_vw"></div>
            <div className="row mt-4 p-0 mx-0">
              <div className="col-12 p-0">
                <InputText
                  inputLabel="Nombre de la carpeta"
                  inputField={moveFolderName}
                  id="nombre-carpeta-mover"
                  changingInput={(event) => setMoveFolderName(event.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="modal-footer footer_modal_vw d-flex justify-content-center justify-content-lg-end gap-2 gap-sm-3 gap-lg-2 px-0 mx-3 mx-lg-5 mb-2 mb-lg-0">
            <button type="button" className="button_vw button_vw--secondary order-2 order-sm-1" data-bs-dismiss="modal" onClick={closeMoveFolderModal}>Cancelar</button>
            <button type="button" className="button_vw button_vw--primary order-1 order-sm-2" data-bs-dismiss="modal" onClick={saveMoveFolder} disabled={hasInvalidName(moveFolderName)} data-track-click="btn-mover-nueva-carpeta-guardar">Guardar</button>
          </div>
        </div>
      </div>
    </div>
  );

  const buildPopUpRename = () => (
    <div
      className={`modal fade modal_vw  ${renameModal ? "show" : ""}`}
      id="modal-renombrar"
      data-keyboard="false"
      data-backdrop="static"
      tabIndex="-1"
      aria-labelledby="renombrarLabel"
      style={renameModal ? { display: "block" } : {}}
      aria-modal="true"
      role="dialog"
    >
      <div className="modal-dialog modal-dialog-centered modal_dialog_fixed--mobile modal_lg_vw" role="document">
        <div className="modal-content modal_content-vw modal_content_fixed--mobile p-0 p-lg-2">
          <div className="modal-header modal_header-vw ">
            <div className="d-flex justify-content-end">
              <button className="button_icon-vw" type="button" data-bs-dismiss="modal" aria-label="Close" onClick={closeRenameModal}>
                <img src={cerrar} alt="cerrar"></img>
              </button>
            </div>
          </div>
          <div className="modal-body mx-3 mx-sm-4 mx-lg-5 p-0 mb-2 mb-sm-4 mb-lg-0">
            <div className="modal-title-container d-flex justify-content-start titulo pb-2">
              <span className={"modal-title-principal-text"}>Renombrar</span>
            </div>
            <div className=" border_vw"></div>
            <div className="row mt-4 p-0 mx-0">
              <div className="col-12 p-0">
                <InputText
                  inputLabel="Nuevo nombre"
                  inputField={renameName}
                  id="nuevo-nombre"
                  changingInput={(event) => setRenameName(event.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="modal-footer footer_modal_vw d-flex justify-content-center justify-content-lg-end gap-2 gap-sm-3 gap-lg-2 px-0 mx-3 mx-lg-5 mb-2 mb-lg-0">
            <button type="button" className="button_vw button_vw--secondary order-2 order-sm-1" data-bs-dismiss="modal" onClick={closeRenameModal}>Cancelar</button>
            <button type="button" className="button_vw button_vw--primary order-1 order-sm-2" data-bs-dismiss="modal" onClick={saveRename} disabled={hasInvalidName(renameName)} data-track-click="btn-renombrar-archivo-guardar">Guardar</button>
          </div>
        </div>
      </div>
    </div>
  );

  const handleRowClick = (archivo) => {
    if (archivo.__isPrefix && archivo.__prefix) {
      setCurrentPrefix(archivo.__prefix);
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Proyecto",
        accessor: "Key",
        Cell: ({ value, row }) => (
          <div className="libreria-item-name-cell">
            <FileTypeIcon item={row.original} className="libreria-item-name-icon" />
            <span>{row.original.__isPrefix ? getProjectName(value) : getLibraryItemName(row.original)}</span>
          </div>
        ),
      },
      {
        Header: "Fecha de creación",
        accessor: "LastModified",
        Cell: ({ value }) => formatDate(value),
      },
      {
        Header: "Tamaño del archivo",
        accessor: "Size",
        Cell: ({ value }) => formatBytes(value),
      },
      {
        Header: "Acción",
        id: "accion",
        Cell: ({ row }) => (
          <div className="d-flex align-items-center justify-content-end">
            <button
              className="button_icon-vw"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onDescargar(getActionTarget(row.original));
              }}
              data-track-click="btn-descargar-archivo"
            >
              <img src={download_icon} alt="descargar" className="px-1" />
            </button>
            <button
              className="button_icon-vw"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                handleEliminar(getActionTarget(row.original));
              }}
              data-track-click="btn-eliminar-archivo"
            >
              <img src={eliminar_icon} alt="eliminar" className="px-1" />
            </button>
            {isImageFile(row.original) && (
              <button
                className="button_icon-vw"
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onPreview(getActionTarget(row.original));
                }}
                data-track-click="btn-vista-previa-archivo"
              >
                <img src={preview_icon} alt="vista previa" className="px-1" />
              </button>
            )}
            {currentPrefix && (
              <button
                className="button_icon-vw"
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onCopiar(getActionTarget(row.original));
                }}
                data-track-click="btn-copiar-archivo"
              >
                <img src={copy_icon} alt="copiar" className="px-1" />
              </button>
            )}
            <div className="tooltip_vw">
              <img src={menu_opc} alt="menu" />
              <div className="tooltiptext p-2 ">
                <button
                  className="button_icon-vw"
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    openMoveModal(getActionTarget(row.original));
                  }}
                  data-track-click="btn-mover-archivo-tooltip"
                >
                  <div className="aling-icon">
                    <img src={mover_icon} alt="mover" className="pr-2" />
                    Mover a
                  </div>
                </button>
                <button
                  className="button_icon-vw"
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    openRenameModal(getActionTarget(row.original));
                  }}
                  data-track-click="btn-renombrar-archivo-tooltip"
                >
                  <div className="aling-icon">
                    <img src={editar_icon} alt="renombrar" />
                    Renombrar
                  </div>
                </button>
              </div>
            </div>
          </div>
        ),
      },
    ],
    [onDescargar, onPreview, onCopiar, handleEliminar, openMoveModal, openRenameModal]
  );
  return (
    <>
      <Table
        columns={columns}
        data={tableRows}
        handleChange={handleChange}
        onNuevo={onNuevo}
        onSubir={onSubir}
        onNuevaCarpeta={onNuevaCarpeta}
        onDescargar={onDescargar}
        onEliminar={handleEliminar}
        onMover={openMoveModal}
        onCopiar={onCopiar}
        onPegar={onPegar}
        onRenombrar={openRenameModal}
        onPreview={onPreview}
        onRowClick={handleRowClick}
        currentPrefix={currentPrefix}
        canPaste={canPaste}
      />
      <ModalWarning
        active={modalEliminar.active}
        title="¿Estás seguro?"
        message={modalEliminar.active ? getDeleteMessage(modalEliminar.item) : ""}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={closeDeleteModal}
      />
      {buildPopUpMove()}
      {buildPopUpMoveFolder()}
      {buildPopUpRename()}
    </>
  );
}

export default TablaArchivos;
